import { forwardRef, type ComponentPropsWithoutRef, type ReactNode } from 'react';

import { cx } from './internal/cx.js';

export interface SwitchProps extends Omit<
  ComponentPropsWithoutRef<'button'>,
  'onChange' | 'role' | 'type' | 'value' | 'children'
> {
  checked: boolean;
  onCheckedChange: (checked: boolean) => void;
  /**
   * Visible label, rendered inside the button so the whole row is the target and names the
   * switch. Without it, name the switch with `aria-label`, `aria-labelledby` or a `<label htmlFor>`.
   */
  label?: ReactNode;
  /** Where the label sits relative to the track (default `end`). */
  labelPlacement?: 'start' | 'end';
}

/**
 * An on/off setting that takes effect immediately — a `<button role="switch">`, not a checkbox,
 * so assistive tech announces it as on/off. Requires `@import '@nrx/ui/theme.css'`.
 */
export const Switch = forwardRef<HTMLButtonElement, SwitchProps>(function Switch(
  { checked, onCheckedChange, label, labelPlacement = 'end', className, onClick, ...props },
  ref,
): React.JSX.Element {
  const text = label == null ? null : <span className="nrx-switch__label">{label}</span>;

  return (
    <button
      ref={ref}
      type="button"
      role="switch"
      aria-checked={checked}
      className={cx('nrx-switch', className)}
      onClick={(event) => {
        onClick?.(event);
        if (!event.defaultPrevented) onCheckedChange(!checked);
      }}
      {...props}
    >
      {labelPlacement === 'start' ? text : null}
      <span className="nrx-switch__track" aria-hidden="true">
        <span className="nrx-switch__thumb" />
      </span>
      {labelPlacement === 'end' ? text : null}
    </button>
  );
});
